/**
 * KI-Angebote (08.10.2026): Typen und Aufrufe fuer /api/v1/angebote.
 * Die Arbeitsfassung liegt im Portal; nach HERO geht erst das Ergebnis.
 */

export type Herkunft = "katalog" | "historie" | "neu" | "manuell"

export type AngebotPosition = {
  id: string
  text: string
  menge: number | null
  einheit: string
  preis: number | null
  herkunft: Herkunft
  product_id?: string
  nr?: string
  vorlagen?: string
  preis_spanne?: [number | null, number | null]
  minuten?: number | null
  hinweis?: string
  unsicher?: boolean
  /** Alternativposition: steht im Angebot zur Wahl, zählt nicht zur Summe (wie in HERO). */
  wahl?: boolean
  /** Stamm-Artikel: Preis im früheren Angebot, wenn er vom aktuellen Stammpreis abweicht. */
  preis_frueher?: number | null
  /** Überschrift des Abschnitts („Gäste-WC“, „Wärmepumpe“, „Sonstiges“) — wird in HERO eine Gruppe. */
  abschnitt?: string
  /** Feste Kennung der Zeile (bleibt über Änderungen). */
  uid?: string
  /** Andere Varianten desselben Teils aus früheren Angeboten — ein Klick tauscht (ohne KI). */
  varianten?: (AngebotPosition & { angeboten?: number })[]
}

/** Antwort zum Anklicken: neu mit fertigen Positionen (Tausch ohne KI), älter als reiner Text. */
export type AngebotAntwort = string | { text: string; annahme?: boolean; positionen?: AngebotPosition[] }
export type AngebotRueckfrage = { frage: string; betrifft?: string[]; antworten: AngebotAntwort[] }

/** Ähnliches früheres Angebot, auf dem der Entwurf beruht. */
export type AngebotVorlage = {
  hero_doc_id: number
  projekttyp: string | null
  wert_netto: number | null
  score: number
  basis?: boolean
  datum?: string | null
  anzahl?: number
  hero_link?: string | null
  /** Welche Abschnitte dieses Angebots als Bausteine dienten (leer = das ganze Angebot). */
  abschnitte?: string[]
}

export type AngebotEntwurf = {
  id: number
  tenant_slug: string
  erstellt_von: string | null
  quelle: string
  project_match_id: number | null
  projekt_name: string | null
  titel: string
  beschreibung: string
  positionen: AngebotPosition[]
  /** Neu: Frage mit Antworten zum Anklicken; ältere Entwürfe haben reinen Text. */
  rueckfragen: (string | AngebotRueckfrage)[]
  /** Teure Zeilen aus der Vorlage, die im Auftrag nicht genannt sind — nicht im Entwurf, ein Klick nimmt sie auf. */
  vorschlaege?: AngebotPosition[]
  vorlagen: AngebotVorlage[]
  status: "entwurf" | "in_hero" | string
  hero_doc_id: number | null
  created_at: string
  updated_at: string
  summe_netto: number
  ohne_preis: number
  hero_link: string | null
  verlauf?: { art: string; text: string; von: string | null; created_at: string }[]
}

export type HeroProjekt = { id: number; nr: string | null; name: string | null; kunde: string; ort: string | null }

async function anfrage<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  })
  const daten = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error((daten as { detail?: string }).detail || `HTTP ${r.status}`)
  return daten as T
}

const q = (tenant: string) => `tenant=${encodeURIComponent(tenant)}`

export const angeboteApi = {
  liste: (tenant: string) =>
    anfrage<{ entwuerfe: AngebotEntwurf[]; historie: { anzahl: number; stand: string | null } }>(
      `/api/v1/angebote?${q(tenant)}`),
  holen: (tenant: string, id: number) => anfrage<AngebotEntwurf>(`/api/v1/angebote/${id}?${q(tenant)}`),
  erstellen: (tenant: string, body: { beschreibung: string; project_match_id?: number | null; projekt_name?: string | null }) =>
    anfrage<AngebotEntwurf>(`/api/v1/angebote?${q(tenant)}`, { method: "POST", body: JSON.stringify(body) }),
  anweisung: (tenant: string, id: number, text: string) =>
    anfrage<AngebotEntwurf>(`/api/v1/angebote/${id}/anweisung?${q(tenant)}`, { method: "POST", body: JSON.stringify({ text }) }),
  aendern: (tenant: string, id: number, body: Record<string, unknown>) =>
    anfrage<AngebotEntwurf>(`/api/v1/angebote/${id}?${q(tenant)}`, { method: "PATCH", body: JSON.stringify(body) }),
  vorlageUebernehmen: (tenant: string, id: number, hero_doc_id: number) =>
    anfrage<AngebotEntwurf>(`/api/v1/angebote/${id}/vorlage?${q(tenant)}`, { method: "POST", body: JSON.stringify({ hero_doc_id }) }),
  antwort: (tenant: string, id: number, frage: number, antwort: number) =>
    anfrage<AngebotEntwurf>(`/api/v1/angebote/${id}/antwort?${q(tenant)}`, { method: "POST", body: JSON.stringify({ frage, antwort }) }),
  variante: (tenant: string, id: number, uid: string, nr: number) =>
    anfrage<AngebotEntwurf>(`/api/v1/angebote/${id}/variante?${q(tenant)}`, { method: "POST", body: JSON.stringify({ uid, nr }) }),
  vorschlagAufnehmen: (tenant: string, id: number, nr: number) =>
    anfrage<AngebotEntwurf>(`/api/v1/angebote/${id}/vorschlag?${q(tenant)}`, { method: "POST", body: JSON.stringify({ nr }) }),
  nachHero: (tenant: string, id: number) =>
    anfrage<AngebotEntwurf>(`/api/v1/angebote/${id}/hero?${q(tenant)}`, { method: "POST" }),
  loeschen: (tenant: string, id: number) =>
    anfrage<{ ok: boolean }>(`/api/v1/angebote/${id}?${q(tenant)}`, { method: "DELETE" }),
  historieAktualisieren: (tenant: string) =>
    anfrage<{ gelesen: number; gespeichert: number; anzahl: number }>(
      `/api/v1/angebote/historie/aktualisieren?${q(tenant)}`, { method: "POST" }),
  projekteSuchen: (tenant: string, suche: string) =>
    anfrage<{ projekte: HeroProjekt[] }>(`/api/v1/angebote/projekte/suche?${q(tenant)}&q=${encodeURIComponent(suche)}`),
}

export function euro(x: number | null | undefined): string {
  if (x === null || x === undefined) return "–"
  return x.toLocaleString("de-DE", { style: "currency", currency: "EUR" })
}

export const HERKUNFT_TEXT: Record<Herkunft, string> = {
  katalog: "HERO-Katalog · Preis setzt HERO",
  historie: "aus früheren Angeboten",
  neu: "neu – bitte prüfen",
  manuell: "von Hand",
}
